import { useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RecordsTable, type Column, type FilterDef } from '../components/RecordsTable/RecordsTable';
import { mcpCall, mcpWrite } from '../lib/mcp';
import { useMcpTools } from '../lib/useMcpTools';
import { useToast } from '../components/Toast/ToastProvider';
import './Settings.css';
import './Privacy.css';

/**
 * Self-service cancellation and delete-my-data requests (cabana.account_requests), 2026-09-26.
 *
 * Mike: "We will likely need admin pages to view and handle the data delete request and view
 * cancellations". A customer starts a request on the site and confirms it from a single-use link
 * emailed to them. This page shows every request by HOUSEHOLD ID only: the MCP action returns no
 * email address and no name, because showing them would put back on screen the data a customer
 * asked to have erased.
 *
 * "Send link to customer" is for a customer who asked by phone or email. It sends them the SAME
 * link the site's form does, so they still confirm from their own inbox. Nothing here cancels or
 * erases anything directly.
 */

type Kind = 'cancel' | 'delete_data';
type Status = 'link_sent' | 'expired' | 'completed' | 'waiting_for_shipment';

interface RequestRow {
  id: number;
  kind: Kind;
  status: Status;
  patient_file_id: string;
  created_at: string;
  expires_at: string;
  used_at: string | null;
  completed_at: string | null;
  result: { cancelled?: number; erased?: string[]; waitingForShipment?: boolean; finishedByDriver?: boolean } | null;
  subscriptions: Array<{ id: number; state: string }>;
}

const KIND_LABEL: Record<Kind, string> = { cancel: 'Cancel', delete_data: 'Delete my data' };
const STATUS_LABEL: Record<Status, string> = {
  link_sent: 'Link sent',
  expired: 'Expired',
  completed: 'Completed',
  waiting_for_shipment: 'Waiting for shipment',
};
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const when = (v: string | null) => (v ? new Date(v).toLocaleString() : '—');

function statusTag(s: Status) {
  const mod = s === 'completed' ? ' records__tag--ok' : s === 'waiting_for_shipment' || s === 'expired' ? ' records__tag--warn' : '';
  return <span className={`records__tag${mod}`}>{STATUS_LABEL[s] ?? s}</span>;
}

/** What was done, in words. The result holds no personal data by construction. */
function resultText(r: RequestRow): string {
  const x = r.result;
  if (!x) return '—';
  const parts: string[] = [];
  if (typeof x.cancelled === 'number') parts.push(`${x.cancelled} subscription${x.cancelled === 1 ? '' : 's'} cancelled`);
  if (x.erased?.length) parts.push(`erased: ${x.erased.join(', ')}`);
  if (x.waitingForShipment) parts.push('name and address kept until a paid box ships');
  if (x.finishedByDriver) parts.push('name and address erased after the box shipped');
  return parts.join('; ') || '—';
}

const COLUMNS: Column<RequestRow>[] = [
  { key: 'created_at', header: 'Requested', sortable: true, render: (r) => when(r.created_at) },
  { key: 'kind', header: 'Kind', render: (r) => <span className="records__tag">{KIND_LABEL[r.kind] ?? r.kind}</span> },
  { key: 'status', header: 'Status', render: (r) => statusTag(r.status) },
  { key: 'patient_file_id', header: 'Household id', render: (r) => <code className="privacy__id">{r.patient_file_id}</code> },
  { key: 'expires_at', header: 'Link expires', sortable: true, render: (r) => when(r.expires_at) },
  { key: 'used_at', header: 'Confirmed', sortable: true, sortAccessor: (r) => r.used_at, render: (r) => when(r.used_at) },
  { key: 'completed_at', header: 'Completed', sortable: true, sortAccessor: (r) => r.completed_at, render: (r) => when(r.completed_at) },
  { key: 'result', header: 'Result', render: (r) => <span className="privacy__result">{resultText(r)}</span> },
  {
    key: 'subscriptions',
    header: 'Subscriptions now',
    render: (r) => (r.subscriptions.length ? r.subscriptions.map((s) => `#${s.id} ${s.state}`).join(', ') : 'none'),
  },
];

const FILTERS: FilterDef<RequestRow>[] = [
  {
    key: 'kind',
    label: 'Kind',
    options: [
      { value: 'all', label: 'All' },
      { value: 'cancel', label: 'Cancel' },
      { value: 'delete_data', label: 'Delete my data' },
    ],
    match: (r, v) => r.kind === v,
  },
  {
    key: 'status',
    label: 'Status',
    options: [
      { value: 'all', label: 'All' },
      ...(Object.keys(STATUS_LABEL) as Status[]).map((s) => ({ value: s, label: STATUS_LABEL[s] })),
    ],
    match: (r, v) => r.status === v,
  },
];

function Counts() {
  const q = useQuery({
    queryKey: ['mcp', 'listAccountRequests', 'counts'],
    queryFn: async () => (await mcpCall<{ statusCounts: Record<Status, number> }>('listAccountRequests', { limit: 1 })).statusCounts,
  });
  if (!q.data) return null;
  return (
    <dl className="privacy__counts" aria-label="Requests by status">
      {(Object.keys(STATUS_LABEL) as Status[]).map((s) => (
        <div className="privacy__count" key={s}>
          <dt>{STATUS_LABEL[s]}</dt>
          <dd>{q.data[s] ?? 0}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Shows only for a role the MCP grants the action to, like every other write control. */
function SendLinkCard() {
  const { can } = useMcpTools();
  const { notify } = useToast();
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<Kind>('delete_data');
  const [household, setHousehold] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  if (!can('sendAccountRequestLink')) return null;

  const id = household.trim();
  const valid = UUID.test(id);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy || !valid) return;
    setBusy(true);
    setError(null);
    setSent(null);
    try {
      const r = await mcpWrite<{ id: number; expiresAt: string }>('sendAccountRequestLink', { kind, patientFileId: id });
      const msg = `${KIND_LABEL[kind]} link emailed to household ${id}. It works once, until ${when(r.expiresAt)}.`;
      setSent(msg);
      setHousehold('');
      notify({ variant: 'success', message: msg });
      await queryClient.invalidateQueries({ queryKey: ['mcp', 'listAccountRequests'] });
    } catch (err) {
      setError((err as Error).message);
      notify({ variant: 'error', message: (err as Error).message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="settings__list">
      <form className="settings__row" onSubmit={submit} aria-labelledby="send-link-title">
        <div className="settings__meta">
          <p className="settings__key" id="send-link-title">Send link to customer</p>
          <p className="settings__desc">
            For a customer who asked by phone or email. This emails the household the same single-use link the
            website sends, so the customer confirms from their own inbox. It does not cancel or delete anything
            by itself. Cancelling stops the automatic reorder and is never refunded.
          </p>
          {sent && <p className="settings__stamp" role="status">{sent}</p>}
          {error && <p className="privacy__error" role="alert">{error}</p>}
        </div>
        <div className="account__form">
          <label className="privacy__field">
            Request
            <select className="settings__input" value={kind} onChange={(e) => setKind(e.target.value as Kind)} disabled={busy}>
              <option value="delete_data">Delete my data</option>
              <option value="cancel">Cancel subscription</option>
            </select>
          </label>
          <label className="privacy__field">
            Household id
            <input
              className="settings__input"
              value={household}
              onChange={(e) => setHousehold(e.target.value)}
              placeholder="00000000-0000-0000-0000-000000000000"
              spellCheck={false}
              autoComplete="off"
              disabled={busy}
              aria-invalid={id !== '' && !valid}
              aria-describedby="household-hint"
            />
          </label>
          <span id="household-hint" className="settings__stamp">
            {id !== '' && !valid ? 'A household id is a UUID.' : 'The patient file id of the household.'}
          </span>
          <button type="submit" className="settings__save" disabled={busy || !valid}>
            {busy ? 'Sending…' : 'Send link to customer'}
          </button>
        </div>
      </form>
    </div>
  );
}

export function PrivacyRequests() {
  return (
    <>
      <SendLinkCard />
      <Counts />
      <RecordsTable<RequestRow>
        title="Cancel and delete-my-data requests"
        intro="Cancellation and delete-my-data requests. A customer asks on the website and confirms from a single-use link emailed to them. A deletion waits for any paid box to ship before the name and address are erased. Households are shown by id only, never by name or email."
        tool="listAccountRequests"
        args={{ limit: 500 }}
        select={(r) => (r as { requests: RequestRow[] }).requests ?? []}
        columns={COLUMNS}
        rowKey={(r) => r.id}
        emptyMessage="No requests yet."
        refreshable
        searchable={{ placeholder: 'Search by household id…', match: (r, q) => r.patient_file_id.toLowerCase().includes(q) }}
        filters={FILTERS}
        defaultSort={{ key: 'created_at', dir: 'desc' }}
      />
    </>
  );
}
